import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Database, Info, X, Mail } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { FormFieldCard } from './FormFieldCard';
import { PageSelector } from '../shared/PageSelector';
import { subscriptionsApi } from '../../../api/modules/subscriptions';

// Email recipient caps by plan tier (must match backend)
const EMAIL_RECIPIENT_CAPS: Record<string, number> = {
  FREE: 1,
  STARTER: 3,
  PROFESSIONAL: 5,
};

export interface FormField {
  id: string;
  label: string;
  identifier: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'checkbox' | 'radio';
  placeholder?: string;
  required: boolean;
  order: number;
  options?: string[];
}

interface FormEditorShape {
  title?: string;
  description?: string;
  fields?: FormField[];
  submitButton?: {
    text: string;
    style: 'primary' | 'secondary' | 'outline';
  };
  emailSettings?: {
    fromEmail?: string;
    toEmail?: string; // DEPRECATED: Use toEmails array instead
    toEmails?: string[]; // NEW: Array of recipient emails (plan-based limits)
    subject?: string;
    successMessage?: string;
    errorMessage?: string;
    redirectOnSuccess?: boolean;
    redirectUrl?: string;
  };
  // Save submissions to database (all plans supported with different limits)
  saveSubmissions?: boolean;
  styling?: {
    spacing: 'compact' | 'normal' | 'spacious';
    layout: 'single-column' | 'two-column';
    alignment: 'left' | 'center' | 'right';
  };
  customColors?: {
    background?: string;
    title?: string;
    label?: string;
    inputBackground?: string;
    inputBorder?: string;
    buttonBackground?: string;
    buttonText?: string;
  };
}

/** Editor state may include extra keys (e.g. form-split `media`, `mediaPosition`). */
export type FormEditorFormState = FormEditorShape & Record<string, unknown>;

interface FormEditorProps {
  formData: FormEditorFormState;
  setFormData: (data: FormEditorFormState) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  /** Theme color mapping key; split form uses `form-split` (same palette as form). */
  colorFieldsComponentType?: string;
}

export const FormEditor: React.FC<FormEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
  colorFieldsComponentType = 'form',
}) => {
  const data = formData || {};
  const fields = data.fields || [];
  const themeColors = useCustomColors();

  const addField = () => {
    const newField: FormField = {
      id: uuidv4(),
      label: 'New Field',
      identifier: `field_${fields.length}`,
      type: 'text',
      placeholder: '',
      required: false,
      order: fields.length,
    };
    setFormData({ ...data, fields: [...fields, newField] });
  };

  const updateField = (index: number, updates: Partial<FormField>) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFormData({ ...data, fields: newFields });
  };

  const removeField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    const reorderedFields = newFields.map((f, i) => ({ ...f, order: i }));
    setFormData({ ...data, fields: reorderedFields });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent(colorFieldsComponentType, themeColors);

  return (
    <div className="space-y-6">
      {/* Form Title and Description */}
      <TitleDescriptionSection data={data} setFormData={setFormData} />

      {/* Form Fields */}
      <FieldsSection
        fields={fields}
        onAddField={addField}
        onUpdateField={updateField}
        onRemoveField={removeField}
      />

      {/* Submit Button Settings */}
      <SubmitButtonSection data={data} setFormData={setFormData} />

      {/* Submission Storage (Database) */}
      <SubmissionStorageSection data={data} setFormData={setFormData} />

      {/* Email Settings */}
      <EmailSettingsSection
        data={data}
        setFormData={setFormData}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
      />

      {/* Styling Options */}
      <StylingSection data={data} setFormData={setFormData} />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize form colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface TitleDescriptionSectionProps {
  data: FormEditorFormState;
  setFormData: (data: FormEditorFormState) => void;
}

const TitleDescriptionSection: React.FC<TitleDescriptionSectionProps> = ({ data, setFormData }) => (
  <>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Form Title *</label>
      <input
        type="text"
        value={data.title || ''}
        onChange={e => setFormData({ ...data, title: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="Enter form title"
        required
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Form Description (optional)
      </label>
      <textarea
        value={data.description || ''}
        onChange={e => setFormData({ ...data, description: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        rows={3}
        placeholder="Enter form description"
      />
    </div>
  </>
);

interface FieldsSectionProps {
  fields: FormField[];
  onAddField: () => void;
  onUpdateField: (index: number, updates: Partial<FormField>) => void;
  onRemoveField: (index: number) => void;
}

const FieldsSection: React.FC<FieldsSectionProps> = ({
  fields,
  onAddField,
  onUpdateField,
  onRemoveField,
}) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <label className="block text-sm font-medium text-gray-700">Form Fields *</label>
      <button
        type="button"
        onClick={onAddField}
        className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
      >
        <Plus size={16} />
        <span>Add Field</span>
      </button>
    </div>
    <div className="space-y-4">
      {fields.map((field, index) => (
        <FormFieldCard
          key={field.id || index}
          field={field}
          index={index}
          onUpdate={updates => onUpdateField(index, updates)}
          onRemove={() => onRemoveField(index)}
        />
      ))}
    </div>
  </div>
);

interface SubmitButtonSectionProps {
  data: FormEditorFormState;
  setFormData: (data: FormEditorFormState) => void;
}

const SubmitButtonSection: React.FC<SubmitButtonSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-sm font-medium text-gray-700 mb-3">Submit Button</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Button Text *</label>
        <input
          type="text"
          value={data.submitButton?.text ?? 'Submit'}
          onChange={e =>
            setFormData({
              ...data,
              submitButton: {
                text: e.target.value,
                style: data.submitButton?.style ?? 'primary',
              },
            })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Submit"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Button Style</label>
        <select
          value={data.submitButton?.style ?? 'primary'}
          onChange={e =>
            setFormData({
              ...data,
              submitButton: {
                text: data.submitButton?.text ?? 'Submit',
                style: e.target.value as 'primary' | 'secondary' | 'outline',
              },
            })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="primary">Primary</option>
          <option value="secondary">Secondary</option>
          <option value="outline">Outline</option>
        </select>
      </div>
    </div>
  </div>
);

interface SubmissionStorageSectionProps {
  data: FormEditorFormState;
  setFormData: (data: FormEditorFormState) => void;
}

const SubmissionStorageSection: React.FC<SubmissionStorageSectionProps> = ({
  data,
  setFormData,
}) => {
  const saveSubmissions = data.saveSubmissions ?? false;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          <Database className="w-5 h-5 text-primary-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="saveSubmissions"
              checked={saveSubmissions}
              onChange={e => setFormData({ ...data, saveSubmissions: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="saveSubmissions" className="text-sm font-medium text-gray-900">
              Save submissions to database
            </label>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Store form submissions in the dashboard for easy viewing and management.
          </p>
          {saveSubmissions && (
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-start space-x-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Submissions will be stored in your dashboard. Free plans can store up to 10
                submissions, Starter up to 500, and Professional up to 5,000.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface EmailSettingsSectionProps {
  data: FormEditorFormState;
  setFormData: (data: FormEditorFormState) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

const EmailSettingsSection: React.FC<EmailSettingsSectionProps> = ({
  data,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const [planName, setPlanName] = useState<string>('FREE');
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);

  // Fetch user's plan to determine email recipient limit
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const subscription = await subscriptionsApi.getCurrentSubscription();
        const name = subscription?.subscription?.planName || subscription?.plan?.name || 'FREE';
        setPlanName(name.toUpperCase());
      } catch (error) {
        // Default to FREE on error
        setPlanName('FREE');
      } finally {
        setIsLoadingPlan(false);
      }
    };
    fetchPlan();
  }, []);

  const emailLimit = EMAIL_RECIPIENT_CAPS[planName] || 1;

  // Get current emails array (merge legacy toEmail with toEmails)
  const getCurrentEmails = (): string[] => {
    const toEmails = data.emailSettings?.toEmails || [];
    const legacyEmail = data.emailSettings?.toEmail || '';

    // If we have toEmails array, use it (this is the new format)
    if (toEmails.length > 0) {
      return toEmails;
    }

    // Otherwise, if there's a legacy email, return it as single-element array
    if (legacyEmail) {
      return [legacyEmail];
    }

    // No emails configured
    return [];
  };

  const currentEmails = getCurrentEmails();

  const updateEmailSettings = (updates: Partial<FormEditorShape['emailSettings']>) => {
    setFormData({
      ...data,
      emailSettings: {
        toEmail: data.emailSettings?.toEmail || '',
        toEmails: data.emailSettings?.toEmails || [],
        subject: data.emailSettings?.subject,
        successMessage: data.emailSettings?.successMessage,
        errorMessage: data.emailSettings?.errorMessage,
        redirectOnSuccess: data.emailSettings?.redirectOnSuccess,
        redirectUrl: data.emailSettings?.redirectUrl,
        ...updates,
      },
    });
  };

  const addEmail = () => {
    if (currentEmails.length >= emailLimit) return;
    updateEmailSettings({ toEmails: [...currentEmails, ''] });
  };

  const updateEmail = (index: number, value: string) => {
    const newEmails = [...currentEmails];
    newEmails[index] = value;
    // Clear legacy toEmail, use only toEmails array
    updateEmailSettings({ toEmail: '', toEmails: newEmails });
  };

  const removeEmail = (index: number) => {
    const newEmails = currentEmails.filter((_, i) => i !== index);
    updateEmailSettings({ toEmail: '', toEmails: newEmails });
  };

  const redirectEnabled = data.emailSettings?.redirectOnSuccess || false;
  const saveSubmissions = data.saveSubmissions ?? false;
  const emailRequired = !saveSubmissions;
  const canAddMore = currentEmails.length < emailLimit;

  // Plan display names for UI
  const planDisplayNames: Record<string, string> = {
    FREE: 'Free',
    STARTER: 'Starter',
    PROFESSIONAL: 'Professional',
  };

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-3">Email Notifications</h3>
      <div className="space-y-4">
        {/* Email Recipients */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-xs font-medium text-gray-600">
              Recipient Emails {emailRequired && '*'}
            </label>
            <span className="text-xs text-gray-500">
              {isLoadingPlan ? '...' : `${currentEmails.length}/${emailLimit} recipients`}
            </span>
          </div>

          <div className="space-y-2">
            {currentEmails.length === 0 ? (
              <button
                type="button"
                onClick={addEmail}
                className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-500 hover:border-primary-400 hover:text-primary-600 flex items-center justify-center space-x-2"
              >
                <Mail size={16} />
                <span>Add email recipient</span>
              </button>
            ) : (
              currentEmails.map((email, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="email"
                    value={email}
                    onChange={e => updateEmail(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="contact@example.com"
                    required={emailRequired && index === 0}
                  />
                  <button
                    type="button"
                    onClick={() => removeEmail(index)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove email"
                  >
                    <X size={18} />
                  </button>
                </div>
              ))
            )}

            {currentEmails.length > 0 && canAddMore && (
              <button
                type="button"
                onClick={addEmail}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
              >
                <Plus size={14} />
                <span>Add another recipient</span>
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500 mt-2">
            {saveSubmissions
              ? 'Optionally receive email notifications when forms are submitted.'
              : 'Form submissions will be sent to these email addresses.'}
          </p>

          {/* Plan limit info */}
          {!isLoadingPlan && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-start space-x-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {planName === 'FREE' && emailLimit === 1 && (
                  <>
                    Your Free plan supports 1 email recipient. Upgrade to Starter for up to 3
                    recipients.
                  </>
                )}
                {planName === 'STARTER' && (
                  <>
                    Your {planDisplayNames[planName]} plan supports up to {emailLimit} email
                    recipients. Upgrade to Professional for up to 5.
                  </>
                )}
                {planName === 'PROFESSIONAL' && (
                  <>
                    Your {planDisplayNames[planName]} plan supports up to {emailLimit} email
                    recipients.
                  </>
                )}
              </span>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Email Subject (optional)
          </label>
          <input
            type="text"
            value={data.emailSettings?.subject || ''}
            onChange={e => updateEmailSettings({ subject: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="New form submission"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Success Message (optional)
            </label>
            <textarea
              value={data.emailSettings?.successMessage || ''}
              onChange={e => updateEmailSettings({ successMessage: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              rows={2}
              placeholder="Thank you for your submission!"
              disabled={redirectEnabled}
            />
            {redirectEnabled && (
              <p className="text-xs text-gray-400 mt-1">Disabled when redirect is enabled.</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Error Message (optional)
            </label>
            <textarea
              value={data.emailSettings?.errorMessage || ''}
              onChange={e => updateEmailSettings({ errorMessage: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              rows={2}
              placeholder="Something went wrong. Please try again."
            />
          </div>
        </div>

        {/* Redirect on Success */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <div className="flex items-center space-x-2 mb-3">
            <input
              type="checkbox"
              id="redirectOnSuccess"
              checked={redirectEnabled}
              onChange={e =>
                updateEmailSettings({
                  redirectOnSuccess: e.target.checked,
                  redirectUrl: e.target.checked ? data.emailSettings?.redirectUrl : '',
                })
              }
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="redirectOnSuccess" className="text-sm font-medium text-gray-700">
              Redirect after successful submission
            </label>
          </div>
          {redirectEnabled && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Redirect URL *</label>
              <PageSelector
                value={data.emailSettings?.redirectUrl || ''}
                onChange={url => updateEmailSettings({ redirectUrl: url })}
                pages={pages}
                isLoadingPages={isLoadingPages}
                currentPageComponents={currentPageComponents}
              />
              <p className="text-xs text-gray-500 mt-1">
                Choose a page or enter a custom URL (e.g., https://example.com/thank-you).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface StylingSectionProps {
  data: FormEditorFormState;
  setFormData: (data: FormEditorFormState) => void;
}

const StylingSection: React.FC<StylingSectionProps> = ({ data, setFormData }) => {
  const updateStyling = (updates: Partial<FormEditorShape['styling']>) => {
    setFormData({
      ...data,
      styling: {
        spacing: data.styling?.spacing || 'normal',
        layout: data.styling?.layout || 'single-column',
        alignment: data.styling?.alignment || 'center',
        ...updates,
      },
    });
  };

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-3">Form Styling</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Spacing</label>
          <select
            value={data.styling?.spacing || 'normal'}
            onChange={e =>
              updateStyling({ spacing: e.target.value as 'compact' | 'normal' | 'spacious' })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="compact">Compact</option>
            <option value="normal">Normal</option>
            <option value="spacious">Spacious</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Layout</label>
          <select
            value={data.styling?.layout || 'single-column'}
            onChange={e =>
              updateStyling({ layout: e.target.value as 'single-column' | 'two-column' })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="single-column">Single Column</option>
            <option value="two-column">Two Column</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Alignment</label>
          <select
            value={data.styling?.alignment || 'center'}
            onChange={e =>
              updateStyling({ alignment: e.target.value as 'left' | 'center' | 'right' })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>
    </div>
  );
};
